import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import topo from '../../data/countries-50m.json';
import { CLUSTERS, VIEWS } from '../../data/markets';

function mercY(lat) {
  return Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
}

function buildView(viewKey) {
  const view = VIEWS[viewKey];
  const world = feature(topo, topo.objects.countries);
  const feats = world.features.filter((f) => f.properties.name !== 'Antarctica');
  let projection;
  if (view.bbox) {
    const { lonMin, lonMax, latMin, latMax } = view.bbox;
    const scale = view.W / (((lonMax - lonMin) * Math.PI) / 180);
    const yMid = (mercY(latMin) + mercY(latMax)) / 2;
    const latMid = (2 * Math.atan(Math.exp(yMid)) - Math.PI / 2) * (180 / Math.PI);
    projection = geoMercator().center([(lonMin + lonMax) / 2, latMid]).scale(scale).translate([view.W / 2, view.H / 2]);
  } else {
    projection = geoMercator().fitExtent([[-10, -44], [view.W + 10, view.H + 56]], {
      type: 'FeatureCollection',
      features: feats,
    });
  }
  const path = geoPath(projection);
  const d = new Map(feats.map((f) => [f.properties.name, path(f)]));
  const project = (lon, lat) => projection([lon, lat]);
  const clusterOf = new Map();
  CLUSTERS.forEach((c) => {
    c.countries.forEach((cname) => {
      const mapName = {
        Canada: 'Canada',
        'United States': 'United States of America',
        'United Arab Emirates': 'United Arab Emirates',
        India: 'India',
        Singapore: 'Singapore',
        Indonesia: 'Indonesia',
      }[cname];
      if (mapName) clusterOf.set(mapName, c.id);
    });
  });
  return { d, project, clusterOf, view };
}

export default function WorldMap({ views, selectedId, hoverId, onSelect, onHover, isMobile }) {
  // views: array of view keys, e.g. ['world'] or ['americas', 'asia']
  const viewKeys = views.join(',');
  const built = useMemo(() => views.map((k) => buildView(k)), [viewKeys]);
  const s = isMobile ? 2.2 : 1;
  const markerR = 3.6 * s;
  const hitR = 16 * s;

  return (
    <>
      {built.map(({ d, project, clusterOf, view }, vi) => {
        const viewKey = views[vi];
        return (
          <svg
            key={viewKey}
            viewBox={`0 0 ${view.W} ${view.H}`}
            className={`block w-full h-auto select-none ${isMobile ? '' : 'mx-auto max-h-[74vh]'}`}
            data-testid={`world-map-svg-${viewKey}`}
            role="img"
            aria-label="Interactive world map of four market clusters"
          >
            <defs>
              <linearGradient id="map-accent" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#713BFF" />
                <stop offset="0.5" stopColor="#C13DDB" />
                <stop offset="1" stopColor="#FF7138" />
              </linearGradient>
            </defs>

            <g>
              {[...d.entries()].map(([name, pathD]) => {
                if (!pathD) return null;
                const cid = clusterOf.get(name);
                const member = !!cid;
                const active = cid && cid === selectedId;
                const hovered = cid && cid === hoverId;
                // On focused (mobile) views, only market countries + land inside
                // the crop matter; the map fill stays understated regardless.
                return (
                  <path
                    key={name}
                    d={pathD}
                    fill={
                      active
                        ? 'rgba(113, 59, 255, 0.22)'
                        : member
                        ? hovered
                          ? '#1b1b2c'
                          : '#171725'
                        : '#0e0e19'
                    }
                    stroke={active ? 'rgba(193, 61, 219, 0.45)' : 'rgba(255,255,255,0.055)'}
                    strokeWidth={0.6}
                    onClick={member ? () => onSelect(cid) : undefined}
                    onMouseEnter={member ? () => onHover(cid) : undefined}
                    onMouseLeave={member ? () => onHover(null) : undefined}
                    style={{ cursor: member ? 'pointer' : 'default', transition: 'fill 0.5s ease, stroke 0.5s ease' }}
                    data-testid={member ? `map-country-${name.toLowerCase().replace(/[^a-z]+/g, '-')}` : undefined}
                  />
                );
              })}
            </g>

            {CLUSTERS.filter((c) => c.labels[viewKey]).map((cluster) => {
              const labelConf = cluster.labels[viewKey];
              if (!labelConf) return null;
              const label = project(labelConf.lon, labelConf.lat);
              if (!label) return null;
              const selected = cluster.id === selectedId;
              const hovered = cluster.id === hoverId;

              return (
                <g key={cluster.id}>
                  {cluster.brands.length === 0 && null}
                  {COUNTRY_MARKERS[cluster.id]?.map((co) => {
                    const p = project(co.lon, co.lat);
                    if (!p) return null;
                    const active = selected || hovered;
                    return (
                      <g
                        key={co.slug}
                        transform={`translate(${p[0]}, ${p[1]})`}
                        onClick={() => onSelect(cluster.id)}
                        onMouseEnter={() => onHover(cluster.id)}
                        onMouseLeave={() => onHover(null)}
                        style={{ cursor: 'pointer' }}
                        data-testid={`map-marker-${co.slug}`}
                      >
                        <circle r={hitR} fill="transparent" />
                        {selected && !isMobile && (
                          <motion.circle
                            r={markerR}
                            fill="none"
                            stroke="#FF7138"
                            strokeWidth={1.2}
                            initial={{ scale: 1, opacity: 0.8 }}
                            animate={{ scale: 3.4, opacity: 0 }}
                            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                          />
                        )}
                        {selected && (
                          <circle r={markerR * 2.1} fill="none" stroke="rgba(255,113,56,0.5)" strokeWidth={0.9} />
                        )}
                        <circle
                          r={markerR}
                          fill={selected ? 'url(#map-accent)' : active ? '#FF7138' : '#8f8fae'}
                          style={{ transition: 'fill 0.3s ease' }}
                        />
                      </g>
                    );
                  })}

                  <g
                    onClick={() => onSelect(cluster.id)}
                    onMouseEnter={() => onHover(cluster.id)}
                    onMouseLeave={() => onHover(null)}
                    style={{ cursor: 'pointer' }}
                    data-testid={`map-label-${cluster.id}`}
                  >
                    <circle cx={label[0]} cy={label[1]} r={hitR} fill="transparent" />
                    <text
                      x={label[0]}
                      y={label[1]}
                      textAnchor={labelConf.anchor || 'middle'}
                      fill={selected ? '#FF7138' : hovered ? '#E2D9FF' : '#A9A9C4'}
                      fontSize={12.5 * s * (isMobile ? 1 : 1)}
                      letterSpacing={2.6 * s}
                      style={{ fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', transition: 'fill 0.3s ease' }}
                    >
                      {cluster.label}
                    </text>
                  </g>
                </g>
              );
            })}
          </svg>
        );
      })}
    </>
  );
}

const COUNTRY_MARKERS = {
  na: [
    { slug: 'canada', lon: -113.5, lat: 56 },
    { slug: 'usa', lon: -98.5, lat: 38.5 },
  ],
  uae: [{ slug: 'uae', lon: 54.2, lat: 23.8 }],
  ind: [{ slug: 'india', lon: 78.6, lat: 22.5 }],
  sea: [
    { slug: 'singapore', lon: 103.8, lat: 1.35 },
    { slug: 'indonesia', lon: 113.8, lat: -2.6 },
  ],
};
